import { Eye, Archive, UserPlus } from 'lucide-react';
import { format } from 'date-fns';
import type { CampaignLead } from '../../types/campaignLead';
import { Button, EmptyState, LoadingSpinner, Badge, Pagination } from '../ui';

interface CampaignLeadListProps {
  campaignLeads: CampaignLead[];
  onView: (campaignLead: CampaignLead) => void;
  onArchive: (campaignLead: CampaignLead) => void;
  onSetRepresentative: (campaignLead: CampaignLead) => void;
  isLoading: boolean;
  pagination?: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
  onPageChange?: (page: number) => void;
}

function CampaignLeadList({
  campaignLeads,
  onView,
  onArchive,
  onSetRepresentative,
  isLoading,
  pagination,
  onPageChange,
}: CampaignLeadListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!campaignLeads || campaignLeads.length === 0) {
    return (
      <EmptyState
        title="Nenhum lead de campanha encontrado"
        description="Crie o primeiro registro de lead em campanha para começar."
      />
    );
  }

  const getScoreColor = (score: number | null | undefined) => {
    if (score === null || score === undefined) return '';

    if (score >= 80) return 'bg-green-50 dark:bg-green-900/20';
    if (score >= 60) return 'bg-blue-50 dark:bg-blue-900/20';
    if (score >= 40) return 'bg-yellow-50 dark:bg-yellow-900/20';
    if (score >= 20) return 'bg-orange-50 dark:bg-orange-900/20';
    return 'bg-red-50 dark:bg-red-900/20';
  };

  const getRowClassName = (score: number | null | undefined) => {
    const baseClass = 'hover:bg-gray-50 dark:hover:bg-gray-700';
    const scoreColor = getScoreColor(score);
    return scoreColor ? `${baseClass} ${scoreColor}` : baseClass;
  };

  return (
    <>
      <div className="mt-8 bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Lead / Contato
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Campanha / Ticket
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Vendedor
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Estágio / Tipo / Ação
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Último Resumo
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {campaignLeads.map(campaignLead => (
                <tr key={campaignLead.id} className={getRowClassName(campaignLead.score)}>
                  <td className="px-3 py-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[250px]">
                      {campaignLead.lead_name || `Lead #${campaignLead.lead_id}`}
                    </div>
                    {campaignLead.notes && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[250px]">
                        {campaignLead.notes}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-900 dark:text-white truncate max-w-[150px]">
                      {campaignLead.campaign_name || `Campanha #${campaignLead.campaign_id}`}
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-900 dark:text-white truncate max-w-[120px]">
                      {campaignLead.representative_name || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <Badge
                      variant={campaignLead.archived_at ? 'default' : 'success'}
                      className="text-xs"
                    >
                      {campaignLead.archived_at ? 'Arquivado' : 'Ativo'}
                    </Badge>
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="info" className="text-xs">
                        {campaignLead.stage}
                      </Badge>
                      <Badge variant="default" className="text-xs">
                        {campaignLead.type}
                      </Badge>
                      <Badge
                        variant="default"
                        className="text-xs"
                      >
                        {campaignLead.action}
                      </Badge>
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {campaignLead.score !== null && campaignLead.score !== undefined
                        ? campaignLead.score
                        : '-'}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                    <div className="max-w-xs truncate">
                      {campaignLead.last_resume || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onView(campaignLead)}
                        title="Visualizar"
                        className="p-1.5"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {!campaignLead.archived_at && (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onSetRepresentative(campaignLead)}
                            title="Atribuir Vendedor"
                            className="p-1.5"
                          >
                            <UserPlus className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onArchive(campaignLead)}
                            title="Arquivar"
                            className="p-1.5"
                          >
                            <Archive className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination && pagination.last_page > 1 && onPageChange && (
        <div className="mt-4">
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.last_page}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </>
  );
}

export default CampaignLeadList;
